import { ChangeDetectionStrategy, Component, computed, signal, ViewEncapsulation } from '@angular/core';
import { MetadataEntityFieldData, MetadataEntityFieldType } from '@jms/model';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { WbValueComponent } from '@workbuddy/ui/v2/components/primitives/value';
import { BaseCellRenderer } from './base.cell.renderer';

export interface ValueCellParams extends ICellRendererParams {
  field: MetadataEntityFieldData;
}

@Component({
  selector: 'value-cell',
  standalone: true,
  imports: [WbValueComponent],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `value-cell { display: flex; align-items: center; height: 100%; width: 100%; min-width: 0; overflow: hidden; } value-cell.value-cell-numeric { justify-content: flex-end; } value-cell.value-cell-numeric > wb-value.wb-value-numeric, value-cell.value-cell-numeric > wb-value.wb-value-numeric > .wb-content { flex: 0 1 auto; text-align: right; }`,
    // Address text owns its wrapping styles, so constrain it explicitly in fixed-height board cells.
    `value-cell > wb-value.wb-value-truncate > wb-value-address .address-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }`,
  ],
  host: { '[class.value-cell-numeric]': '$numeric()' },
  // All board-table values render as text (or text + swatch/avatar); ellipsis when the fixed column width clips.
  template: `<wb-value [field]="$field()" [data]="$data()" [value]="$value()" [truncate]="true" />`,
})
export class ValueCellRenderer extends BaseCellRenderer<any, ValueCellParams> {

  protected $field = signal<MetadataEntityFieldData | null>(null);
  protected $data = signal<any>(null);
  protected $value = signal<any>(null);
  protected $numeric = computed(() => {
    const type = this.$field()?.type;
    const scalar = Array.isArray(type) ? type[0] : type;
    return scalar === MetadataEntityFieldType.Number || scalar === MetadataEntityFieldType.Currency || scalar === MetadataEntityFieldType.Percentage;
  });

  public displayLogic(params: ValueCellParams): void {

    this.$field.set(params.field ?? null);
    this.$data.set(params.data);
    // AG Grid resolves the value from the colDef `field` (the fully-qualified column path, which
    // matches the row shape). Pass it explicitly so wb-value renders it directly instead of
    // re-deriving from field.path — custom fields are keyed by a bare path (customFields.<id>) that
    // doesn't match the queue row's nested 'entity.customFields.<id>' shape.
    this.$value.set(params.value ?? null);
  }
}
